import { useState, useEffect } from "react";
import { Box, Button, Checkbox, Stack, useMediaQuery } from "@mui/material";
import { Seo } from "src/components/seo";
import Paper from "@mui/material/Paper";
import { styled } from "@mui/material/styles";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "src/store";
import { thunks } from "src/thunks/booking-process";
import { useRouter } from "src/hooks/use-router";
// Guard
import { withUserIsInDBRoleGuard } from "src/hocs/with-role-based-guard";
// Components
import { renderLogo } from "./booking-process-components";
import {
  CustomButton,
  CustomDivider,
  CustomTypography,
} from "src/components/inputs";
import { LoaderIcon } from "react-hot-toast";

const BrandBookingSelect = () => {
  const dispatch = useDispatch();
  const params = useParams();
  const salesCampaignId = params?.sc_id;
  const userId = params?.id;
  // Check for ?tmw=1 in the URL
  const isTmwCrewFlow =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("tmw") === "1";
  const router = useRouter();
  const mdUp = useMediaQuery((theme) => theme.breakpoints.up("md"));
  const mobileView = !mdUp;
  const screenHeight = useMediaQuery("(min-height:1050px)");

  const {
    activeBrands,
    activeBrandsLoading,
    targetBrands,
    targetBrandsLoading,
    buyingGroups,
    storeName,
    hubspotError,
    bookingLinks,
    contactInfo,
  } = useSelector((state) => state.bookingProcess);

  const [selectedActiveBrands, setSelectedActiveBrands] = useState([]);
  const [selectedTargetBrands, setSelectedTargetBrands] = useState([]);

  const [activeHoveredBrand, setActiveHoveredBrand] = useState("");
  const [targetHoveredBrand, setTargetHoveredBrand] = useState("");
  const [hoveredLink, setHoveredLink] = useState(0);

  const [doNotSendConfirmationEmail, setDoNotSendConfirmationEmail] =
    useState(false);

  // State for selected emails in buying groups
  const [selectedEmails, setSelectedEmails] = useState(() => {
    // By default, select the primary contact's email if available
    if (contactInfo && contactInfo.email) {
      return new Set([contactInfo.email]);
    }
    return new Set();
  });
  const [selectedBuyingGroups, setSelectedBuyingGroups] = useState([]);
  // Use Set for selectedBuyingGroups for efficient add/remove
  // If you want to persist as array, convert to array on submit
  // Initialize as Set for correct logic
  useEffect(() => {
    setSelectedBuyingGroups(new Set());
  }, []);

  useEffect(() => {
    dispatch(thunks.getCustomerBrands(userId, salesCampaignId, isTmwCrewFlow));
    // dispatch(thunks.getCusNumbers(userId, salesCampaignId));
    // dispatch(thunks.getCategories(userId, salesCampaignId));
    dispatch(thunks.getBookingLinks(userId));
    dispatch(thunks.getContactInfo(userId));
  }, []);

  useEffect(() => {
    setSelectedActiveBrands(activeBrands);
    setSelectedTargetBrands(targetBrands);
  }, [activeBrands, targetBrands]);

  // Update selectedEmails if contactInfo.email changes
  useEffect(() => {
    if (contactInfo && contactInfo.email) {
      setSelectedEmails(new Set([contactInfo.email]));
    }
  }, [contactInfo?.email]);

  useEffect(() => {
    if (contactInfo && contactInfo.email) {
      setSelectedBuyingGroups(new Set(buyingGroups.map((group) => group.id)));
    }
  }, [buyingGroups]);

  const handleSubmitSelectedBrands = () => {
    const chosenSelectedActiveBrands = selectedActiveBrands.filter(
      (brand) => brand.selected,
    );
    const chosenSelectedTargetBrands = selectedTargetBrands.filter(
      (brand) => brand.selected,
    );

    const selectedBrands = [
      ...chosenSelectedActiveBrands,
      ...chosenSelectedTargetBrands,
    ];

    if (!selectedBrands.length) {
      return;
    }

    dispatch(thunks.setSelectedBrands(selectedBrands));

    // Find out the active brands that got deselected

    // Later save these brands until the booking completion stage and then send them
    // to the backend to set the deal status to the relevant deal to SKIPPED

    const secondBrandNames = selectedBrands.map((item) => item.brandName);
    const uniqueBrands = activeBrands.filter(
      (item) => !secondBrandNames.includes(item.brandName),
    );

    const skippedBrandNames = uniqueBrands.map((item) => {
      return item.brandName;
    });

    // Store the skipped active brands
    dispatch(thunks.setSkippedBrands(skippedBrandNames));

    // TMW Flow
    dispatch(thunks.setIsTmwFlow(isTmwCrewFlow));
    dispatch(thunks.setSelectedEmails(Array.from(selectedEmails)));
    dispatch(thunks.setDoNotSendConfirmationEmail(doNotSendConfirmationEmail));

    if (
      buyingGroups &&
      selectedBuyingGroups instanceof Set &&
      !doNotSendConfirmationEmail
    ) {
      const selectedStoreNames = Array.from(selectedBuyingGroups)
        .map((groupId) => {
          const group = buyingGroups.find((g) => g.id === groupId);
          return group && group.properties && group.properties.store_name
            ? group.properties.store_name
            : null;
        })
        .filter(Boolean);

      dispatch(thunks.setSelectedBuyingGroups(selectedStoreNames));
    }

    router.push("calendar");
  };

  const toggleBrandSelection = (
    event,
    brandName,
    selectedBrands,
    setSelectedBrands,
  ) => {
    event.stopPropagation();
    const updatedBrands = selectedBrands.map((brand) => {
      if (brand.brandName === brandName) {
        return {
          ...brand,
          selected: !brand.selected, // Update the selected state to true
        };
      }
      return brand; // Keep other brands unchanged
    });

    setSelectedBrands(updatedBrands);
  };

  const renderBrands = (selectedBrands, setSelectedBrands, brandType) => {
    if (!selectedBrands || !selectedBrands.length) return null;

    const brandComponents = selectedBrands.map((brand, index) => (
      <Button
        disableRipple
        disableTouchRipple
        style={{
          fontFamily: "tmwCustomFont",
          letterSpacing: 4,
          fontSize: 20,
          fontWeight: 400,
          textAlign: "center",
          boxShadow: "none",
          flexDirection: "row",
          cursor: "pointer",
          userSelect: "none",
          padding: 0,
          color:
            ((brandType === "active" &&
              activeHoveredBrand === brand.brandName) ||
              (brandType === "target" &&
                targetHoveredBrand === brand.brandName)) &&
            brand.selected &&
            !mobileView
              ? "#BDCB92"
              : ((brandType === "active" &&
                    activeHoveredBrand === brand.brandName) ||
                    (brandType === "target" &&
                      targetHoveredBrand === brand.brandName)) &&
                  !brand.selected &&
                  !mobileView
                ? "#B7B7B7"
                : brand.selected
                  ? "#7CAF00"
                  : "black",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        sx={{
          "&:hover": {
            background: "none",
          },
        }}
        key={brand.id}
        onClick={(e) =>
          toggleBrandSelection(
            e,
            brand.brandName,
            selectedBrands,
            setSelectedBrands,
          )
        }
        onMouseEnter={() =>
          brandType === "active"
            ? setActiveHoveredBrand(brand.brandName)
            : setTargetHoveredBrand(brand.brandName)
        }
        onMouseLeave={() =>
          brandType === "active"
            ? setActiveHoveredBrand("")
            : setTargetHoveredBrand("")
        }
      >
        {brand.brandName}
        {selectedBrands.length !== index + 1 && ","}
      </Button>
    ));

    return (
      <Stack
        style={{
          width: "120%",
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          alignItems: "center",
          columnGap: 20,
          rowGap: 10,
          flexWrap: "wrap",
          padding: "10px",
        }}
      >
        {brandComponents}
      </Stack>
    );
  };

  const renderBrandSelection = () => {
    let chosenActive = true;
    let chosenTarget = true;

    selectedActiveBrands.map((brand) => {
      if (brand.selected === true) {
        chosenActive = false;
      }
    });

    selectedTargetBrands.map((brand) => {
      if (brand.selected === true) {
        chosenTarget = false;
      }
    });

    return (
      <>
        <p
          style={{
            fontFamily: "tmwCustomFont",
            letterSpacing: 3,
            fontSize: mobileView ? 10 : 11,
            textTransform: "uppercase",
            marginBottom: screenHeight ? "15px" : "12px",
            color: "black",
          }}
        >
          SELECT YOUR BRANDS
        </p>
        {renderBrands(selectedActiveBrands, setSelectedActiveBrands, "active")}
        {!!selectedTargetBrands?.length && (
          <p
            style={{
              fontFamily: "tmwCustomFont",
              letterSpacing: 3,
              fontSize: mobileView ? 10 : 11,
              textTransform: "uppercase",
              margin: screenHeight ? "20px 0 15px 0" : "24px 0 12px 0",
              textAlign: "center",
              color: "black",
            }}
          >
            YOU MAY ALSO LIKE TO SELECT
          </p>
        )}
        {renderBrands(selectedTargetBrands, setSelectedTargetBrands, "target")}
      </>
    );
  };

  const renderLinks = () => {
    return (
      <>
        <p
          style={{
            fontFamily: "tmwCustomFont",
            letterSpacing: 3,
            fontSize: mobileView ? 7 : 9,
            textAlign: "center",
            lineHeight: "200%",
            textTransform: "uppercase",
            marginTop: mobileView ? "50px" : screenHeight ? "30px" : "36px",
            marginBottom: screenHeight ? "" : "0px",
            width: mobileView ? "90%" : "800px",
          }}
        >
          Please review Tomorrow Brand Portfolio Presentation and Showroom
          Flyers at the links below for specific details on dates and locations
          for each brand.
        </p>
        <p
          style={{
            display: "flex",
            flexDirection: "column",
            fontFamily: "tmwCustomFont",
            letterSpacing: 3,
            fontSize: mobileView ? 7 : 10,
            textAlign: "center",
            textTransform: "uppercase",
            marginTop: screenHeight ? "25px" : "12px",
            marginBottom: screenHeight ? "15px" : "24px",
            gap: "7px",
          }}
        >
          {bookingLinks?.brandPortfolioLink && (
            <a
              href={bookingLinks.brandPortfolioLink}
              target="_blank"
              rel="noreferrer"
              onMouseEnter={() => setHoveredLink(1)}
              onMouseLeave={() => setHoveredLink(0)}
              style={{ color: hoveredLink === 1 ? "#BEBEBE" : "black" }}
            >
              BRAND PORTFOLIO
            </a>
          )}
          {bookingLinks?.showroomFliersLink && (
            <a
              href={bookingLinks.showroomFliersLink}
              target="_blank"
              rel="noreferrer"
              onMouseEnter={() => setHoveredLink(2)}
              onMouseLeave={() => setHoveredLink(0)}
              style={{ color: hoveredLink === 2 ? "#BEBEBE" : "black" }}
            >
              SHOWROOM FLYERS
            </a>
          )}
        </p>
      </>
    );
  };

  const renderSubmitButton = () => {
    let disabledFlag = true;

    selectedActiveBrands.map((brand) => {
      if (brand.selected === true) {
        disabledFlag = false;
      }
    });

    selectedTargetBrands.map((brand) => {
      if (brand.selected === true) {
        disabledFlag = false;
      }
    });

    return (
      <CustomButton
        label="Next"
        onClick={handleSubmitSelectedBrands}
        smallButton
        filled
        bookingSubmit={!disabledFlag}
        blackText
        style={{
          marginTop: mobileView ? "5px" : screenHeight ? "20px" : "24px",
          padding: "17px 0",
          width: "100px",
          borderColor: "#7CAF00",
        }}
        disabled={disabledFlag}
      />
    );
  };

  // Render contacts under each buying group
  const renderBuyingGroups = () => {
    if (!buyingGroups || !buyingGroups.length) return null;
    const handleCheckboxChange = (email) => {
      setSelectedEmails((prev) => {
        const next = new Set(prev);
        if (next.has(email)) {
          next.delete(email);
        } else {
          next.add(email);
        }
        return next;
      });
    };

    // Prevent all buying groups from being deselected
    const handleBuyingGroupChange = (groupId) => {
      setSelectedBuyingGroups((prev) => {
        const safePrev = prev instanceof Set ? prev : new Set();
        const next = new Set(safePrev);
        if (next.has(groupId)) {
          // Only allow deselect if more than 1 group is currently selected
          if (next.size > 1) {
            next.delete(groupId);
          }
        } else {
          next.add(groupId);
        }
        return next;
      });
    };
    return (
      <div
        style={{
          width: "100%",
          marginTop: 0,
          marginBottom: 24,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <div
          style={{
            borderBottom: "1px solid #E6E6E6",
            padding: "20px 32px",
            width: "50%",
            background: "white",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Stack style={{ maxWidth: 550 }}>
            <p
              style={{
                fontFamily: "tmwCustomFont",
                fontSize: 12,
                fontWeight: 500,
                marginTop: 0,
                marginBottom: 20,
                letterSpacing: 2,
                textTransform: "uppercase",
                textAlign: "center",
              }}
            >
              Select contacts to send email confirmations:
            </p>
            {buyingGroups.map((group, index) => {
              const selectedGroup =
                selectedBuyingGroups instanceof Set
                  ? selectedBuyingGroups.has(group.id)
                  : false;

              return (
                <div
                  key={`${group.id}-${index}`}
                  style={{
                    marginBottom: 18,
                    width: "100%",
                    border:
                      selectedGroup && !doNotSendConfirmationEmail
                        ? "1.5px solid #7CAF00"
                        : "1px solid #E6E6E6",
                    padding:
                      selectedGroup && !doNotSendConfirmationEmail
                        ? "15px 0"
                        : "16px 0",
                    transition: "border-color 0.3s",
                  }}
                >
                  <Stack
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 5,
                      borderBottom: "1px solid #E6E6E6",
                      paddingBottom: 10,
                    }}
                  >
                    <Checkbox
                      type="checkbox"
                      checked={selectedGroup}
                      onChange={() => handleBuyingGroupChange(group.id)}
                      disabled={
                        (selectedBuyingGroups.size === 1 && selectedGroup) ||
                        doNotSendConfirmationEmail
                      }
                      style={{
                        marginRight: 8,
                        color:
                          (selectedBuyingGroups.size === 1 && selectedGroup) ||
                          doNotSendConfirmationEmail
                            ? "#E6E6E6"
                            : "black",
                        transition: "color 0.3s",
                      }}
                    />
                    <Stack
                      style={{
                        alignItems: "flex-start",
                        color: doNotSendConfirmationEmail ? "#E6E6E6" : "black",
                        transition: "color 0.3s",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 11,
                          marginBottom: 4,
                          textAlign: "center",
                          fontFamily: "tmwCustomFont",
                          textTransform: "uppercase",
                          letterSpacing: 2,
                        }}
                      >
                        <span>Store Name: </span>
                        <span style={{ fontWeight: 600 }}>
                          {group.properties.store_name}
                        </span>
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          marginBottom: 4,
                          textAlign: "center",
                          fontFamily: "tmwCustomFont",
                          textTransform: "uppercase",
                          letterSpacing: 2,
                        }}
                      >
                        <span>Buying group: </span>
                        <span style={{ fontWeight: 600 }}>
                          {group.properties.name}
                        </span>
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          marginBottom: 4,
                          textAlign: "center",
                          fontFamily: "tmwCustomFont",
                          textTransform: "uppercase",
                          letterSpacing: 2,
                        }}
                      >
                        Id: <span style={{ fontWeight: 600 }}>{group.id}</span>
                      </div>
                    </Stack>
                  </Stack>
                  {group.contacts && group.contacts.length > 0 ? (
                    <ul
                      style={{
                        marginTop: 5,
                        marginBottom: 0,
                        padding: "3px 0",
                        paddingTop: "6px",
                        minWidth: "75%",
                        maxWidth: "100%",
                        listStyle: "none",
                        textAlign: "center",
                        overflowWrap: "break-word",
                        justifySelf: "center",
                      }}
                    >
                      {group.contacts.map((contact) => (
                        <li
                          key={contact.id}
                          style={{
                            fontSize: 11,
                            marginBottom: 6,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontFamily: "tmwCustomFont",
                            letterSpacing: 2,
                          }}
                        >
                          <Checkbox
                            type="checkbox"
                            checked={selectedEmails.has(contact.email)}
                            onChange={() => handleCheckboxChange(contact.email)}
                            style={{
                              marginRight: 8,
                              color: doNotSendConfirmationEmail
                                ? "#E6E6E6"
                                : "black",
                              transition: "color 0.3s",
                            }}
                            disabled={doNotSendConfirmationEmail}
                          />
                          <span
                            style={{
                              color: doNotSendConfirmationEmail
                                ? "#E6E6E6"
                                : "black",
                              transition: "color 0.3s",
                            }}
                          >
                            {contact.email}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div
                      style={{
                        fontSize: 12,
                        color: "#888",
                        textAlign: "center",
                      }}
                    >
                      No contacts
                    </div>
                  )}
                </div>
              );
            })}
            <Stack
              style={{
                flexDirection: "row",
                alignItems: "center",
                cursor: "pointer",
                userSelect: "none",
              }}
              onClick={() =>
                setDoNotSendConfirmationEmail(!doNotSendConfirmationEmail)
              }
            >
              <Checkbox
                type="checkbox"
                checked={doNotSendConfirmationEmail}
                style={{ marginRight: 8, color: "black" }}
              />
              <CustomTypography
                style={{ fontSize: 10 }}
                message="Do not send email confirmations for this booking"
              />
            </Stack>
          </Stack>
        </div>
      </div>
    );
  };

  return (
    <Stack style={{ overflowX: "auto" }}>
      <Seo title="Booking: Brand Select" />
      <span
        style={{
          marginTop: mobileView ? "-15px" : "",
          marginLeft: mobileView ? "-15px" : "",
          marginBottom: mobileView ? "15px" : "",
        }}
      >
        {renderLogo(mobileView)}
      </span>
      <Box
        component="main"
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          padding: mobileView ? "50px 0 100px 0" : "0 0 25px 0",
        }}
      >
        <Stack
          style={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            fontSize: 20,
          }}
        >
          <p
            style={{
              fontFamily: "tmwCustomFont",
              letterSpacing: 4,
              fontSize: mobileView ? 18 : 20,
              margin: mobileView
                ? "-75px 0 0 0"
                : screenHeight
                  ? "-100px 0 0 0"
                  : "-75px 0 0 0",
            }}
          >
            {contactInfo.firstname
              ? `HELLO, ${contactInfo.firstname.toUpperCase()}`
              : storeName
                ? `HELLO, ${storeName}`
                : "HELLO"}
          </p>
          {(activeBrandsLoading ||
            targetBrandsLoading ||
            !!selectedActiveBrands?.length) && (
            <>
              <p
                style={{
                  fontFamily: "tmwCustomFont",
                  letterSpacing: 3,
                  fontSize: mobileView ? 10 : 12,
                  textTransform: "uppercase",
                  textAlign: "center",
                  marginBottom: screenHeight ? "35px" : "24px",
                }}
              >
                Please book your appointment
              </p>
              <Stack
                style={{
                  width: "100%",
                  marginBottom: screenHeight ? "20px" : "24px",
                }}
              >
                <CustomDivider />
              </Stack>
            </>
          )}
          {!activeBrandsLoading &&
            !targetBrandsLoading &&
            !selectedActiveBrands?.length &&
            !selectedTargetBrands?.length && (
              <p
                style={{
                  fontFamily: "tmwCustomFont",
                  letterSpacing: 3,
                  fontSize: mobileView ? 10 : 10,
                  textTransform: "uppercase",
                  marginBottom: "35px",
                }}
              >
                No brands available
              </p>
            )}
          {isTmwCrewFlow && (
            <>
              {/* <p
                style={{
                  fontFamily: "tmwCustomFont",
                  letterSpacing: 2,
                  fontSize: mobileView ? 12 : 14,
                  color: "#7CAF00",
                  marginBottom: "10px",
                  marginTop: "-10px",
                  textTransform: "uppercase",
                }}
              >
                tmw crew flow
              </p> */}
              {renderBuyingGroups()}
            </>
          )}
          {!hubspotError ? (
            activeBrandsLoading || targetBrandsLoading ? (
              <LoaderIcon
                style={{ width: "22px", height: "22px", marginTop: "30px" }}
              />
            ) : (
              (selectedActiveBrands?.length !== 0 ||
                selectedTargetBrands?.length !== 0) && (
                <Stack
                  style={{
                    width: "75%",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {renderBrandSelection()}
                  {renderLinks()}
                  {renderSubmitButton()}
                </Stack>
              )
            )
          ) : (
            <Stack
              style={{
                width: mdUp ? "50%" : "75%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "40vh",
              }}
            >
              <CustomTypography
                message={hubspotError}
                style={{ fontSize: 14, textAlign: "center" }}
              />
            </Stack>
          )}
        </Stack>
      </Box>
    </Stack>
  );
};

export default withUserIsInDBRoleGuard(BrandBookingSelect);
